/* Timbrica — /flight-compensation: интерфейс.
 *
 * Вся арифметика и юриспруденция живут в engine.js и config/passenger-rights.php.
 * Здесь только форма, вывод и письмо — чтобы правила можно было проверять
 * тестами без браузера, а интерфейс менять, не трогая правила.
 *
 * ⚠️ ПОРЯДОК БЛОКОВ РЕЗУЛЬТАТА — ЧАСТЬ ОТВЕТА, А НЕ ОФОРМЛЕНИЕ.
 * Человек открывает тул в двух разных состояниях: он либо СИДИТ В АЭРОПОРТУ
 * прямо сейчас, либо разбирает старую историю дома. Первому деньги через месяц
 * не помогут, ему нужны еда, гостиница и телефон — то есть право на заботу,
 * которое действует ДАЖЕ при форс-мажоре и о котором почти никто не знает.
 * Поэтому забота показывается ВСЕГДА, сразу под суммой и выше всего
 * остального; письмо, эскалация и справочные таблицы свёрнуты в ящики.
 */
import { assess } from './engine.js';
import { buildLetter } from './letter.js';
import { loadAirports, search, label, countryName } from './airports.js';

const CFG = window.FLIGHT_COMP_CFG || {};
const T = CFG.t || {};
const RULES = CFG.rules || {};
const AIRLINES = CFG.airlines || {};
const LETTERS = CFG.letters || {};
const LOCALE = CFG.locale || 'en';
const SLUG = 'flight-compensation';

// Что нужно модулю аэропортов: сам датасет, база локализованных имён и локаль.
const AIRPORT_CFG = {
  airportsUrl: CFG.airportsUrl,
  namesUrlBase: CFG.namesUrlBase,
  locale: LOCALE,
};

const tr = (k) => (T[k] != null ? T[k] : k);
const $ = (id) => document.getElementById(id);
const ae = (ev, p) => { try { window.__ae && window.__ae(ev, p || {}); } catch (e) {} };
const stage = (n) => { try { window.__stage && window.__stage(n); } catch (e) {} };

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
const fill = (k, map) => {
  let s = tr(k);
  for (const [n, v] of Object.entries(map || {})) s = s.split(':' + n).join(String(v));
  return s;
};

const state = {
  from: null,
  to: null,
  airline: '',
  airlineCountry: '',
  airlineSize: null,
  scenario: 'delay',
  arrivalDelayH: null,
  cancelNoticeDays: null,
  reroutedArrivalDelayH: null,
  cause: 'unknown',
  flightDate: '',
  flightNumber: '',
  oneWayFare: null,
  passengers: 1,
  last: null,
  letterLang: 'local',
};

/* ── Выпадающий список ────────────────────────────────────────────────────
 * Два лейна одной разметки.
 *
 * Новый: `popover` уносит список в top layer, а CSS anchor positioning
 * ставит его под полем. Это единственный способ, которым список ГАРАНТИРОВАННО
 * оказывается над соседней панелью: у панелей `isolation:isolate` и
 * `backdrop-filter`, каждый из которых создаёт свой контекст наложения, и
 * никакой z-index изнутри панели наружу не действует.
 *
 * Старый: обычный absolute внутри поля, как было. Работает, пока рядом нет
 * панели с фильтром, — и именно на этом прошлая версия уже один раз
 * ловилась («список видно, а нажать нельзя»).
 *
 * Обе возможности проверяются по отдельности: браузер может уметь popover и
 * не уметь якоря (Firefox), и тогда top layer без адреса поставил бы список в
 * угол экрана. Такой лейн нам не нужен — он хуже старого.
 */
const CAN_ANCHOR = (() => {
  try {
    return typeof HTMLElement !== 'undefined'
      && HTMLElement.prototype.hasOwnProperty('popover')
      && CSS.supports('anchor-name', '--a')
      && CSS.supports('top', 'anchor(bottom)');
  } catch (e) { return false; }
})();

function mountCombo(inputId, listId, opts) {
  const input = $(inputId);
  const list = $(listId);
  if (!input || !list) return;
  let items = [];
  let active = -1;
  let open = false;

  if (CAN_ANCHOR) {
    const anchor = '--' + inputId;
    input.style.setProperty('anchor-name', anchor);
    list.style.setProperty('--fc-anchor', anchor);
  } else {
    list.removeAttribute('popover');
  }

  const close = () => {
    if (!open) return;
    open = false;
    active = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    if (CAN_ANCHOR) { try { list.hidePopover(); } catch (e) {} }
    list.hidden = true;
  };

  const show = () => {
    if (open) return;
    open = true;
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    if (CAN_ANCHOR) { try { list.showPopover(); } catch (e) {} }
  };

  const render = () => {
    list.innerHTML = items.map((a, i) => {
      const row = opts.row(a);
      return '<li class="fc-opt' + (i === active ? ' cur' : '') + '" role="option" id="' + listId + '-' + i +
        '" aria-selected="' + (i === active ? 'true' : 'false') + '">' +
        '<span class="fc-opt-code">' + esc(row.code) + '</span>' +
        '<span class="fc-opt-main">' + esc(row.main) + '</span>' +
        '<span class="fc-opt-cc">' + esc(row.aside) + '</span></li>';
    }).join('');
    if (!items.length) { close(); return; }
    show();
    if (active >= 0) input.setAttribute('aria-activedescendant', listId + '-' + active);
  };

  // mousedown, а не click: blur поля успел бы закрыть список раньше клика.
  list.addEventListener('mousedown', (e) => {
    const li = e.target.closest('.fc-opt');
    if (!li) return;
    e.preventDefault();
    const i = Array.prototype.indexOf.call(list.children, li);
    if (items[i]) { opts.pick(items[i]); close(); }
  });

  input.addEventListener('input', async () => {
    opts.clear();
    items = await opts.query(input.value);
    active = -1;
    render();
    recompute();
  });

  input.addEventListener('keydown', (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { active = Math.min(active + 1, items.length - 1); render(); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { active = Math.max(active - 1, 0); render(); e.preventDefault(); }
    else if (e.key === 'Enter' && active >= 0) { opts.pick(items[active]); close(); e.preventDefault(); }
    else if (e.key === 'Escape') { close(); e.preventDefault(); }
    else if (e.key === 'Tab') close();
  });

  input.addEventListener('blur', () => setTimeout(close, 120));
  if (opts.warm) input.addEventListener('focus', opts.warm, { once: true });
}

function mountAirportCombo(inputId, listId, key) {
  mountCombo(inputId, listId, {
    warm: () => { loadAirports(AIRPORT_CFG).catch(() => {}); },
    clear: () => { state[key] = null; },
    query: async (q) => {
      try {
        await loadAirports(AIRPORT_CFG);
      } catch (e) {
        // Датасет не доехал — поле остаётся текстовым, тул не умирает целиком.
        window.__toast && window.__toast(tr('airports_failed'), 'error');
        return [];
      }
      return search(q, 8);
    },
    row: (a) => ({ code: a.iata, main: label(a, LOCALE), aside: countryName(a.cc, LOCALE) }),
    pick: (a) => {
      state[key] = a;
      $(inputId).value = label(a, LOCALE);
      ae('fc_airport_pick', { side: key, iata: a.iata });
      recompute();
    },
  });
}

/* Реестр перевозчиков в удобной для поиска форме.
 *
 * ⚠️ Синонимы написания — не украшение. Российские перевозчики записаны
 * кириллицей, остальные латиницей, а пассажир смотрит в посадочный талон, где
 * стоит «AEROFLOT». Без синонимов «Aeroflot» не находился ВООБЩЕ, и тул
 * предлагал выбрать страну перевозчика руками — то есть просил пассажира
 * ответить на тот самый вопрос, ради которого поле и существует. А страна
 * решает, какой режим применяется: это разница между 600 € и 25 ₽ в час.
 */
const CARRIERS = Object.entries(AIRLINES).map(([code, v]) => ({
  code,
  name: v[0],
  cc: v[1],
  size: v[2] || null,
  hay: [v[0]].concat(v[3] || []).map((s) => s.toLowerCase()),
}));

/** Перевозчик по коду рейса: «SU 1234», «su1234», «U6 123» → запись реестра. */
function carrierFromFlightNo(s) {
  const m = String(s || '').trim().toUpperCase().match(/^([A-Z][A-Z0-9]|[0-9][A-Z])\s*\d/);
  return m ? CARRIERS.find((c) => c.code === m[1]) || null : null;
}

function mountAirlineCombo() {
  const entries = CARRIERS;
  const ccWrap = $('fc-airline-cc-wrap');

  mountCombo('fc-airline', 'fc-airline-list', {
    clear: () => {
      state.airline = $('fc-airline').value.trim();
      state.airlineCountry = '';
      state.airlineSize = null;
    },
    query: async (q) => {
      const n = q.trim().toLowerCase();
      if (n.length < 2) { if (ccWrap) ccWrap.hidden = true; return []; }
      const hits = entries
        .filter((e) => e.code.toLowerCase() === n || e.hay.some((h) => h.includes(n)))
        // Совпадение с начала слова выше совпадения в середине: «air» не должен
        // выносить «Ryanair» вперёд «Air France».
        .sort((a, z) => (z.hay.some((h) => h.startsWith(n)) ? 1 : 0) - (a.hay.some((h) => h.startsWith(n)) ? 1 : 0))
        .slice(0, 8);
      // Не нашли — показываем выбор страны руками. Это честнее, чем угадать:
      // от страны перевозчика зависит, какой режим вообще применяется.
      if (ccWrap) ccWrap.hidden = hits.length > 0 || n.length < 3;
      return hits;
    },
    row: (e) => ({ code: e.code, main: e.name, aside: countryName(e.cc, LOCALE) }),
    pick: (e) => {
      state.airline = e.name;
      state.airlineCountry = e.cc;
      state.airlineSize = e.size;
      $('fc-airline').value = e.name;
      if (ccWrap) ccWrap.hidden = true;
      ae('fc_airline_pick', { code: e.code, cc: e.cc });
      recompute();
    },
  });

  const ccSel = $('fc-airline-cc');
  if (ccSel) {
    ccSel.addEventListener('change', () => {
      state.airlineCountry = ccSel.value;
      ae('fc_airline_country_manual', { cc: ccSel.value });
      recompute();
    });
  }
}

// ── Сбор формы ──────────────────────────────────────────────────────────────
function num(id) {
  const el = $(id);
  if (!el || el.value === '') return null;
  const n = parseFloat(String(el.value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function readForm() {
  state.scenario = (document.querySelector('input[name="fc-scenario"]:checked') || {}).value || 'delay';
  state.arrivalDelayH = num('fc-delay');
  state.cancelNoticeDays = num('fc-notice');
  state.reroutedArrivalDelayH = num('fc-reroute-delay');
  state.oneWayFare = num('fc-fare');
  state.passengers = Math.max(1, Math.min(9, Math.round(num('fc-pax') || 1)));
  state.cause = ($('fc-cause') || {}).value || 'unknown';
  state.flightDate = ($('fc-date') || {}).value || '';
  state.flightNumber = ($('fc-flightno') || {}).value || '';
}

function syncScenarioFields() {
  const s = state.scenario;
  const show = (id, on) => { const el = $(id); if (el) el.hidden = !on; };
  show('fc-row-delay', s === 'delay' || s === 'denied' || s === 'missed');
  show('fc-row-notice', s === 'cancel');
  show('fc-row-reroute', s === 'delay' || s === 'cancel');
  show('fc-row-fare', s === 'denied' || s === 'downgrade' || state.airlineCountry === 'RU' || state.airlineCountry === 'KR');
  // Часы имеют смысл только там, где право считается от опоздания.
  const clock = $('fc-clock');
  if (clock) clock.hidden = !(s === 'delay' || s === 'missed');
}

/* Перевод «во сколько должны были / во сколько прилетели» в часы.
 *
 * Ночной перелёт пересекает полночь, поэтому отрицательная разница читается
 * как «прилетел на следующие сутки», а не как ошибка ввода. Разницу больше
 * 20 часов такой трактовкой уже не объяснить — там человек скорее перепутал
 * поля местами, и об этом надо сказать, а не подставлять 23 часа в расчёт. */
function syncClock() {
  const out = $('fc-clock-out');
  const a = ($('fc-sched') || {}).value;
  const b = ($('fc-actual') || {}).value;
  if (!out) return;
  if (!a || !b) { out.textContent = ''; return; }
  const mins = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  let d = mins(b) - mins(a);
  if (d < 0) d += 24 * 60;
  if (d > 20 * 60) { out.textContent = tr('delay_negative'); return; }
  // ⚠️ Округлять здесь НЕЛЬЗЯ. Порог — ровно три часа, и он же граница между
  // «600 €» и «ничего»: округление до получаса превращает 2 ч 50 мин в
  // выплату, которой нет, а 3 ч 10 мин — в отказ, которого нет. Первая версия
  // клала в поле 5.5 и писала рядом «5.7» — два разных числа из одного ввода.
  const hours = Math.round((d / 60) * 100) / 100;
  const field = $('fc-delay');
  if (field) field.value = String(hours);
  out.textContent = fill('delay_computed', { hours: String(hours) });
  ae('fc_clock_used', { hours });
  recompute();
}

// ── Вывод ───────────────────────────────────────────────────────────────────
function money(amount, currency) {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(LOCALE, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch (e) { return Math.round(amount) + ' ' + currency; }
}

function recompute() {
  readForm();
  syncScenarioFields();

  if (!state.from || !state.to) {
    $('fc-answer').innerHTML = '<p class="fc-empty">' + esc(tr('need_route')) + '</p>';
    $('fc-care').hidden = true;
    $('fc-more').innerHTML = '';
    $('fc-letter-drawer').hidden = true;
    if (barEl) barEl.hidden = true;
    state.last = null;
    return;
  }

  const trip = {
    from: state.from,
    to: state.to,
    airlineCountry: state.airlineCountry,
    airlineSize: state.airlineSize,
    airlineName: state.airline,
    scenario: state.scenario,
    arrivalDelayH: state.arrivalDelayH,
    cancelNoticeDays: state.cancelNoticeDays,
    reroutedArrivalDelayH: state.reroutedArrivalDelayH,
    oneWayFare: state.oneWayFare,
    segmentFare: state.oneWayFare,
    passengers: state.passengers,
    cause: state.cause,
    flightDate: state.flightDate,
    flightNumber: state.flightNumber,
    claimCountry: state.from.cc,
  };
  const res = assess(trip, RULES);
  state.last = { res, trip };
  renderAnswer(res);
  updateBar(res);
  renderCare(res);
  renderDrawers(res, trip);
  prepareLetter(res, trip);
  stage('result');
}

function renderAnswer(res) {
  const primary = res.regimes.find((r) => r.key === res.primary);
  const symbolic = !!(primary && primary.basis && primary.basis.symbolic);
  const pax = res.passengers;
  const out = [];

  if (primary && primary.amount > 0) {
    const each = money(primary.amount, primary.currency);
    const total = money(primary.amount * pax, primary.currency);
    out.push('<div class="fc-answer-wrap">');
    out.push('<p class="tc-answer-l">' + esc(tr('answer_label')) + '</p>');
    out.push('<div class="fc-sum ' + (symbolic ? 'symbolic' : 'money') + '">' + esc(pax > 1 ? total : each) + '</div>');
    // ⚠️ При нескольких пассажирах крупное число — ИТОГ, а под ним ставка на
    // человека. Иначе «600 €» на экране и «2400 €» в письме выглядят как
    // разные расчёты, и пассажир не знает, какое число называть перевозчику.
    if (pax > 1) out.push('<p class="fc-each">' + esc(fill('pax_each', { amount: each, n: pax })) + '</p>');
    out.push('<p class="fc-law">' + esc(fill('under_law', { law: primary.law })) + '</p>');
    if (primary.reduced) out.push('<p class="fc-law">' + esc(tr('reduced_half')) + '</p>');
    if (symbolic) out.push('<p class="tc-msg warn">' + esc(tr('symbolic_note')) + '</p>');
    if (primary.confidence !== 'high') {
      out.push('<span class="fc-conf">' + esc(tr('confidence_' + primary.confidence)) + '</span>');
    }
    out.push('</div>');
  } else {
    const why = primary ? tr('blocked_' + (primary.blocked || 'none')) : tr('blocked_no_regime');
    out.push('<div class="fc-answer-wrap">');
    out.push('<p class="tc-answer-l">' + esc(tr('answer_label')) + '</p>');
    out.push('<div class="fc-sum zero">' + esc(tr('no_money')) + '</div>');
    out.push('<p class="fc-law">' + esc(why) + '</p>');
    out.push('</div>');
  }

  // Пограничное расстояние: 0.3% арифметики стоят двухсот евро.
  if (res.borderline) {
    out.push('<p class="tc-msg warn">' + esc(fill('borderline', { km: res.borderline.km, edge: res.borderline.edge })) + '</p>');
  }
  // Причина: работает ли отговорка.
  // ⚠️ Ссылку на дело Суда ЕС показываем ТОЛЬКО в режимах, где она имеет силу.
  // Российскому пассажиру «так решил суд (C-549/07)» — это чужая норма, и она
  // подталкивает написать письмо, которое не работает.
  const primaryCjeu = primary && primary.cjeuCaseLaw;
  if (res.cause.extraordinary === false && res.cause.case && primaryCjeu) {
    out.push('<p class="tc-msg good">' + esc(fill('cause_not_extraordinary', { case: res.cause.case })) + '</p>');
  } else if (res.cause.extraordinary === true) {
    out.push('<p class="tc-msg warn">' + esc(tr('cause_extraordinary')) + '</p>');
  }

  const meta = [fill('distance', { km: res.km })];
  if (res.limitation) meta.push(fill('limitation', { years: res.limitation.years, date: res.limitation.date }));
  out.push('<div class="fc-meta">' + meta.map((m) => '<span>' + esc(m) + '</span>').join('') + '</div>');

  out.push('<div class="tc-actions" style="margin-top:12px">' +
    '<button type="button" class="tc-btn" id="fc-make-letter">' + esc(tr('make_letter')) + '</button>' +
    '</div>');

  const el = $('fc-answer');
  el.innerHTML = out.join('');
  el.classList.remove('tc-flash');
  void el.offsetWidth;
  el.classList.add('tc-flash');
  $('fc-make-letter').addEventListener('click', openLetter);
}

/* Бегунок с ответом.
 *
 * Показывается ровно тогда, когда сам ответ ушёл за край экрана, — это
 * IntersectionObserver, а не медиазапрос: на широком экране с открытым
 * длинным ящиком ответ тоже уезжает, и порог по ширине окна такого случая не
 * видит. Полоска не дублирует живую область (`aria-hidden`): скринридер уже
 * получил ответ из #fc-answer, и второе объявление той же суммы — шум. */
let barEl = null;
let barIO = null;

function updateBar(res) {
  const primary = res && res.regimes.find((r) => r.key === res.primary);
  const has = !!(primary && primary.amount > 0);
  if (!has) { if (barEl) barEl.hidden = true; return; }

  const symbolic = !!(primary.basis && primary.basis.symbolic);
  const total = money(primary.amount * res.passengers, primary.currency);
  if (!barEl) {
    barEl = document.createElement('button');
    barEl.type = 'button';
    barEl.className = 'fc-bar';
    barEl.hidden = true;
    barEl.setAttribute('aria-hidden', 'true');
    barEl.tabIndex = -1;
    barEl.addEventListener('click', () => {
      $('fc-answer').scrollIntoView({ block: 'nearest' });
      ae('fc_bar_tap', {});
    });
    document.body.appendChild(barEl);
  }
  barEl.innerHTML = '<span class="fc-bar-l">' + esc(tr('answer_label')) + '</span>' +
    '<span class="fc-bar-v' + (symbolic ? '' : ' money') + '">' + esc(total) + '</span>' +
    '<span class="fc-bar-go" aria-hidden="true">↑</span>';

  if (!barIO && 'IntersectionObserver' in window) {
    barIO = new IntersectionObserver((entries) => {
      const off = entries[0] && !entries[0].isIntersecting;
      if (barEl) barEl.hidden = !off || !state.last || !hasMoney(state.last.res);
    }, { threshold: 0 });
    barIO.observe($('fc-answer'));
  }
}

function hasMoney(res) {
  const p = res && res.regimes.find((r) => r.key === res.primary);
  return !!(p && p.amount > 0);
}

function careText(i) {
  switch (i.kind) {
    case 'meals': return fill('care_meals', { h: i.afterH });
    case 'drinks': return fill('care_drinks', { h: i.afterH });
    case 'hot_meal': return fill('care_hot_meal', { h: i.afterH, day: i.everyDayH ?? '', night: i.everyNightH ?? '' });
    case 'hotel': return fill('care_hotel', { day: i.dayH ?? '', night: i.nightH ?? '' });
    case 'hotel_overnight': return tr('care_hotel_overnight');
    // Порог есть не у всех режимов: ФАП-82 даёт звонки ПОСЛЕ двух часов, а
    // европейская ст. 9 — вместе с питанием, без своего часа.
    case 'calls': return i.afterH
      ? fill('care_calls_after', { n: i.count, h: i.afterH })
      : fill('care_calls', { n: i.count });
    case 'comms': return fill('care_comms', { h: i.afterH });
    case 'mother_child': return fill('care_mother_child', { age: i.age });
    case 'baggage_storage': return tr('care_baggage');
    default: return i.kind;
  }
}

function renderCare(res) {
  const el = $('fc-care');
  const regime = res.regimes.find((r) => r.care && r.care.items.length);
  if (!regime) { el.hidden = true; return; }
  el.hidden = false;
  // Перерисовывается только СПИСОК: поле расходов живёт в разметке рядом, и
  // введённое в него переживает любое изменение формы.
  $('fc-care-body').innerHTML =
    '<div class="tc-head"><h2 class="tc-title">' + esc(tr('care_title')) + '</h2></div>' +
    (regime.care.regardlessOfCause ? '<p class="tc-hint">' + esc(tr('care_regardless')) + '</p>' : '') +
    '<ul>' + regime.care.items.map((i) => '<li>' + esc(careText(i)) + '</li>').join('') + '</ul>';
}

/* Ящики: то, что нужно НЕ ВСЕМ и не сразу.
 * Порядок — по срочности, а не по важности: «что собрать» тает вместе с
 * рейсом (табло погаснет, стойка закроется), а таблица режимов подождёт. */
function renderDrawers(res, trip) {
  const primary = res.regimes.find((r) => r.key === res.primary);
  const box = [];

  // 1. Что собрать прямо сейчас.
  const items = Array.isArray(T.evidence_items) ? T.evidence_items : [];
  if (items.length) {
    box.push(drawer('ev', '📎', tr('evidence_title'), tr('evidence_sub'),
      '<p class="tc-hint">' + esc(tr('evidence_note')) + '</p>' +
      '<ul class="fc-check">' + items.map((s, i) =>
        '<li><label><input type="checkbox" data-ev="' + i + '"><span>' + esc(s) + '</span></label></li>').join('') +
      '</ul>'));
  }

  // 2. Возврат вместо перелёта — решение, которое принимают на месте.
  if (primary && primary.refund) {
    const r = primary.refund;
    box.push(drawer('rf', '↩️', tr('refund_title'),
      r.available ? '' : fill('refund_after', { h: r.afterH }),
      '<p class="tc-hint">' + esc(tr('refund_body')) + '</p>' +
      '<ul class="fc-check"><li><label><input type="checkbox" id="fc-want-refund"><span>' +
      esc(tr('refund_check')) + '</span></label></li></ul>'));
  }

  // 3. Страховка. Для российского пассажира это чаще всего БОЛЬШЕ денег, чем
  //    компенсация: 25 ₽ в час против фиксированной выплаты по полису. Спрос
  //    это подтверждает — «страховка задержка рейса» ищут вдвое чаще, чем
  //    «компенсация за задержку рейса» (замер Wordstat 07.09.2026: 11 190
  //    против 5 407 показов в месяц), а ни один калькулятор об этом не говорит.
  box.push(drawer('ins', '🛡️', tr('insurance_title'), tr('insurance_sub'),
    '<p class="tc-hint" style="line-height:1.6">' + esc(tr('insurance_body')) + '</p>'));

  // 4. Если откажут или промолчат.
  const body = escalationFor(trip.from?.cc);
  if (body) {
    const name = body.body || tr('escalation_neb');
    const url = body.url || body.neb_list || '';
    const link = (href) => ' <a href="' + esc(href) + '" target="_blank" rel="noopener nofollow">' +
      esc(tr('escalation_open')) + '</a>';
    // Экранируем СНАЧАЛА, подставляем разметку ПОТОМ: иначе название органа с
    // амперсандом («ANAC · consumidor.gov.br») уехало бы в HTML как есть.
    // Ссылка стоит в КОНЦЕ шага, а не посреди фразы: «жалоба в DGAC открыть —
    // его заключение бесплатно» читалось как обрывок. Сначала предложение
    // целиком, потом действие.
    const steps = [
      '<li>' + esc(fill('escalation_step1', { days: 14 })) + '</li>',
      '<li>' + esc(tr('escalation_step2')).split(':body').join('<b>' + esc(name) + '</b>') +
        ' — ' + esc(body.neb_binding ? tr('escalation_binding_yes') : tr('escalation_binding_no')) + '.' +
        (url ? link(url) : '') + '</li>',
      '<li>' + esc(tr('escalation_step3')) +
        (body.small_claims ? link(body.small_claims) : '') + '</li>',
    ];
    box.push(drawer('esc', '⚖️', tr('escalation_title'), tr('escalation_sub'),
      '<ol class="fc-steps">' + steps.join('') + '</ol>' +
      '<div class="tc-actions" style="margin-top:12px">' +
      '<button type="button" class="tc-btn ghost sm" id="fc-ics">' + esc(tr('remind_btn')) + '</button></div>' +
      '<p class="tc-hint" style="margin-top:6px">' + esc(tr('remind_desc')) + '</p>'));
  }

  // 5. Все применимые режимы — справка, а не действие.
  if (res.regimes.length > 1) {
    const rows = res.regimes.map((r) =>
      '<tr' + (r.key === res.primary ? ' class="now"' : '') + '><td>' + esc(r.law) + '</td><td class="num">' +
      esc(r.amount > 0 ? money(r.amount, r.currency) : tr('no_money_short')) + '</td><td>' +
      esc(r.confidence === 'high' ? '—' : tr('confidence_' + r.confidence)) + '</td></tr>').join('');
    box.push(drawer('all', '🌍', tr('all_regimes_title'), fill('regimes_sub', { n: res.regimes.length }),
      '<p class="tc-hint">' + esc(tr('all_regimes_note')) + '</p>' +
      '<div class="tc-tablewrap"><table class="tc-table"><thead><tr><th>' + esc(tr('col_law')) +
      '</th><th class="num">' + esc(tr('col_amount')) + '</th><th>' + esc(tr('col_confidence')) +
      '</th></tr></thead><tbody>' + rows + '</tbody></table></div>' +
      (res.regimes.some((r) => r.key === 'eu261') ? '<p class="tc-note">' + esc(tr('reform_note')) + '</p>' : '')));
  }

  // ⚠️ Ящики перерисовываются на КАЖДОЕ изменение формы, а внутри них живут
  // поля, которые заполняет человек. Без переноса состояния сумма расходов и
  // галочка возврата стирались от одного нажатия на стрелку «часов» — и это
  // выглядело бы не как сброс, а как «оно само не сохраняет».
  const host = $('fc-more');
  const wasOpen = new Set(Array.from(host.querySelectorAll('details[open]')).map((d) => d.dataset.k));
  const keepRefund = !!($('fc-want-refund') || {}).checked;
  host.innerHTML = box.join('');
  host.querySelectorAll('details').forEach((d) => { if (wasOpen.has(d.dataset.k)) d.open = true; });
  if ($('fc-want-refund')) $('fc-want-refund').checked = keepRefund;
  wireDrawers();
}

/**
 * Куда идти, когда перевозчик отказал.
 *
 * У страны вылета либо есть собственный надзорный орган, либо она в ЕС — и
 * тогда работает общая европейская запись (список NEB + суд малых исков).
 * Порядок важен: своя запись всегда сильнее общей.
 */
function escalationFor(cc) {
  const map = CFG.escalation || {};
  if (!cc) return null;
  const inEu = (RULES.eu_states || []).includes(cc);
  // Своя запись СИЛЬНЕЕ общей, но не заменяет её целиком: у страны ЕС есть и
  // собственный орган, и общий список с порядком малых исков. Раньше запись
  // страны отменяла общую, и вместе с ней пропадал третий шаг лестницы.
  if (map[cc]) return inEu ? { ...(map.eu || {}), ...map[cc] } : map[cc];
  return inEu ? (map.eu || null) : null;
}

function drawer(key, ico, title, sub, inner) {
  return '<details class="tc-drawer" data-k="' + key + '"><summary>' +
    '<span class="tc-drawer-ico" aria-hidden="true">' + ico + '</span>' +
    '<span class="fc-dh"><span>' + esc(title) + '</span>' +
    (sub ? '<span class="tc-drawer-sub">' + esc(sub) + '</span>' : '') + '</span>' +
    '</summary><div class="tc-drawer-body">' + inner + '</div></details>';
}

function wireDrawers() {
  // Отметки в списке «что собрать» переживают перезагрузку: человек собирает
  // документы частями, между заходами на страницу.
  const saved = loadChecks();
  document.querySelectorAll('#fc-more [data-ev]').forEach((el) => {
    el.checked = saved.includes(Number(el.dataset.ev));
    el.addEventListener('change', () => {
      const on = Array.from(document.querySelectorAll('#fc-more [data-ev]:checked')).map((x) => Number(x.dataset.ev));
      saveChecks(on);
      ae('fc_evidence_check', { n: on.length });
    });
  });
  const ref = $('fc-want-refund');
  if (ref) ref.addEventListener('change', () => prepareLetter(state.last?.res, state.last?.trip));
  document.querySelectorAll('#fc-more details').forEach((d) => {
    d.addEventListener('toggle', () => { if (d.open) ae('fc_drawer_open', { k: d.dataset.k }); });
  });
  const ics = $('fc-ics');
  if (ics) ics.addEventListener('click', downloadReminder);
}

const CHECK_KEY = 'evidence';
function loadChecks() {
  try { return JSON.parse(window.__toolSettings.load(SLUG, CHECK_KEY, '[]')) || []; } catch (e) { return []; }
}
function saveChecks(v) {
  try { window.__toolSettings.save(SLUG, CHECK_KEY, JSON.stringify(v)); } catch (e) {}
}

// ── Письмо ──────────────────────────────────────────────────────────────────
function letterFields() {
  return {
    passengerName: ($('fc-name') || {}).value || '',
    bookingRef: ($('fc-pnr') || {}).value || '',
    contactEmail: ($('fc-email') || {}).value || '',
    companions: ($('fc-companions') || {}).value
      ? String($('fc-companions').value).split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean)
      : [],
    careExpenses: ($('fc-expenses') || {}).value || '',
    wantRefund: !!($('fc-want-refund') || {}).checked,
  };
}

function prepareLetter(res, trip) {
  if (!res || !trip) return;
  const drawerEl = $('fc-letter-drawer');
  drawerEl.hidden = false;
  const primary = res.regimes.find((r) => r.key === res.primary);
  $('fc-letter-sub').textContent = primary && primary.amount > 0
    ? fill('pax_total', { amount: money(primary.amount * res.passengers, primary.currency) })
    : tr('letter_ready_sub');

  if (!$('fc-letter-text')) {
    const pax = res.passengers;
    $('fc-letter-body').innerHTML =
      '<div class="fc-form">' +
      field('fc-name', tr('name_label'), 'text', 'name') +
      field('fc-pnr', tr('pnr_label'), 'text', 'off', 'ABC123') +
      field('fc-email', tr('email_label'), 'email', 'email') +
      '</div>' +
      '<div class="tc-f fc-names" id="fc-companions-wrap" style="margin-top:9px"' + (pax > 1 ? '' : ' hidden') + '>' +
      '<label class="tc-f-l" for="fc-companions">' + esc(tr('companions_label')) + '</label>' +
      '<input type="text" id="fc-companions" class="tc-in" autocomplete="off">' +
      '<p class="tc-hint" style="margin-top:4px">' + esc(tr('companions_hint')) + '</p></div>' +
      '<p class="tc-hint" style="margin:10px 0 6px">' + esc(tr('letter_hint')) + '</p>' +
      (LETTERS.en
        ? '<div class="fc-lang" role="group" aria-label="' + esc(tr('letter_lang_label')) + '">' +
          '<button type="button" data-lang="local" class="on">' + esc(tr('letter_lang_local')) + '</button>' +
          '<button type="button" data-lang="en">' + esc(tr('letter_lang_en')) + '</button></div>'
        : '') +
      '<textarea id="fc-letter-text" class="tc-in fc-letter-area" rows="16" spellcheck="false" style="margin-top:8px"></textarea>' +
      '<div class="tc-actions" style="margin-top:10px">' +
      '<button type="button" class="tc-btn" id="fc-copy">' + esc(tr('copy')) + '</button>' +
      '<button type="button" class="tc-btn ghost" id="fc-download">' + esc(tr('download')) + '</button>' +
      '</div>';
    wireLetter();
  }
  const cw = $('fc-companions-wrap');
  if (cw) cw.hidden = res.passengers < 2;
  writeLetter();
}

function field(id, lab, type, ac, ph) {
  return '<div class="tc-f"><label class="tc-f-l" for="' + id + '">' + esc(lab) + '</label>' +
    '<input type="' + type + '" id="' + id + '" class="tc-in" autocomplete="' + ac + '"' +
    (ph ? ' placeholder="' + esc(ph) + '"' : '') + '></div>';
}

function wireLetter() {
  ['fc-name', 'fc-pnr', 'fc-email', 'fc-companions'].forEach((id) => {
    const el = $(id);
    if (el) el.addEventListener('input', writeLetter);
  });
  document.querySelectorAll('.fc-lang button').forEach((b) => {
    b.addEventListener('click', () => {
      state.letterLang = b.dataset.lang;
      document.querySelectorAll('.fc-lang button').forEach((x) => x.classList.toggle('on', x === b));
      ae('fc_letter_lang', { lang: state.letterLang });
      writeLetter();
    });
  });
  $('fc-copy').addEventListener('click', async () => {
    const ta = $('fc-letter-text');
    try {
      await navigator.clipboard.writeText(ta.value);
      window.__toast && window.__toast(tr('copied'), 'success');
      ae('fc_letter_copy', { regime: state.last?.res?.primary, lang: state.letterLang });
    } catch (e) {
      ta.select();
      window.__toast && window.__toast(tr('copy_manual'), 'warning');
    }
  });
  $('fc-download').addEventListener('click', () => {
    const blob = new Blob([$('fc-letter-text').value], { type: 'text/plain;charset=utf-8' });
    const base = fill('letter_filename', { flight: state.last?.trip?.flightNumber || '' });
    const name = (window.__outputName ? window.__outputName.sanitize(base) : 'claim') + '.txt';
    window.__downloadFile && window.__downloadFile(blob, name);
    ae('fc_letter_download', { regime: state.last?.res?.primary, lang: state.letterLang });
  });
}

function writeLetter() {
  const ta = $('fc-letter-text');
  if (!ta || !state.last) return;
  const { res, trip } = state.last;
  const escBody = escalationFor(trip.from?.cc);
  const strings = (state.letterLang === 'en' && LETTERS.en) ? LETTERS.en : LETTERS.local;
  const letter = buildLetter(res, {
    ...trip,
    ...letterFields(),
    escalationBody: escBody ? (escBody.body || tr('escalation_neb')) : null,
  }, strings || {});
  // Тема уже стоит внутри тела письма. Приклеивать её ещё и сверху значило
  // начинать письмо одной и той же строкой дважды — так и было до правки.
  ta.value = letter.body;
}

function openLetter() {
  const d = $('fc-letter-drawer');
  if (!d) return;
  d.open = true;
  // block:'nearest' — прокрутка только если ящик за экраном; страницу не рвёт.
  d.scrollIntoView({ block: 'nearest' });
  const n = $('fc-name');
  if (n) n.focus({ preventScroll: true });
  ae('fc_letter_built', { regime: state.last?.res?.primary, pax: state.last?.res?.passengers });
  stage('letter');
}

/* Напоминание в календарь.
 *
 * Претензия — это не документ, а процесс: перевозчик молчит две недели, и
 * ровно в этот момент дело обычно и умирает — не потому, что нет прав, а
 * потому, что о нём забыли. Календарь решает это без аккаунта и без нашей
 * почты, поэтому файл, а не подписка. */
function downloadReminder() {
  const { res, trip } = state.last || {};
  if (!res) return;
  const pad = (n) => String(n).padStart(2, '0');
  const day = (d) => d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate());
  const plus = (n) => { const d = new Date(); d.setUTCDate(d.getUTCDate() + n); return d; };
  const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const route = (trip.from?.iata || '') + '-' + (trip.to?.iata || '');
  const ev = (uid, start, title, desc) => [
    'BEGIN:VEVENT', 'UID:' + uid + '@timbrica', 'DTSTAMP:' + stamp,
    'DTSTART;VALUE=DATE:' + day(start), 'DTEND;VALUE=DATE:' + day(new Date(start.getTime() + 864e5)),
    'SUMMARY:' + ics(title), 'DESCRIPTION:' + ics(desc), 'END:VEVENT',
  ];
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Timbrica//flight-compensation//EN', 'CALSCALE:GREGORIAN'];
  lines.push(...ev('fc-reply-' + Date.now(), plus(15),
    fill('remind_summary', { route }), tr('remind_desc')));
  if (res.limitation) {
    const d = new Date(res.limitation.date + 'T00:00:00Z');
    // За месяц до конца срока, а не в сам день: в день истечения напоминание
    // уже бесполезно.
    d.setUTCDate(d.getUTCDate() - 30);
    if (d.getTime() > Date.now()) {
      lines.push(...ev('fc-limit-' + Date.now(), d,
        fill('remind_summary', { route }),
        fill('limitation', { years: res.limitation.years, date: res.limitation.date })));
    }
  }
  lines.push('END:VCALENDAR');
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  window.__downloadFile && window.__downloadFile(blob, 'timbrica-' + route + '.ics');
  window.__toast && window.__toast(tr('remind_done'), 'success');
  ae('fc_reminder', { regime: res.primary });
}

/** Экранирование по RFC 5545: запятая, точка с запятой и перевод строки. */
function ics(s) {
  return String(s || '').replace(/\\/g, '\\\\').replace(/[;,]/g, (c) => '\\' + c).replace(/\r?\n/g, '\\n');
}

// ── Старт ───────────────────────────────────────────────────────────────────
function init() {
  mountAirportCombo('fc-from', 'fc-from-list', 'from');
  mountAirportCombo('fc-to', 'fc-to-list', 'to');
  mountAirlineCombo();

  document.querySelectorAll('input[name="fc-scenario"]').forEach((el) => {
    el.addEventListener('change', () => { ae('fc_scenario', { v: el.value }); recompute(); });
  });
  ['fc-delay', 'fc-notice', 'fc-reroute-delay', 'fc-fare', 'fc-date', 'fc-flightno', 'fc-pax']
    .forEach((id) => { const el = $(id); if (el) el.addEventListener('input', recompute); });

  // Номер рейса УЖЕ содержит код перевозчика. Заставлять человека вводить
  // «SU 1234», а потом отдельно набирать «Аэрофлот» — это лишний вопрос с
  // готовым ответом; а поскольку от страны перевозчика зависит применимый
  // режим, незаполненное поле стоит дороже лишнего клика.
  // Заполняем ТОЛЬКО пустое поле: явный выбор человека не перебиваем.
  const fno = $('fc-flightno');
  if (fno) {
    fno.addEventListener('input', () => {
      const air = $('fc-airline');
      if (!air || air.value.trim()) return;
      const c = carrierFromFlightNo(fno.value);
      if (!c) return;
      air.value = c.name;
      state.airline = c.name;
      state.airlineCountry = c.cc;
      state.airlineSize = c.size;
      const ccWrap = $('fc-airline-cc-wrap');
      if (ccWrap) ccWrap.hidden = true;
      ae('fc_airline_from_flightno', { code: c.code });
      recompute();
    });
  }
  const causeSel = $('fc-cause');
  if (causeSel) causeSel.addEventListener('change', recompute);
  // Расходы в письмо, но НЕ в расчёт: компенсацию они не меняют, а полную
  // перерисовку вызывать на каждый символ незачем.
  const exp = $('fc-expenses');
  if (exp) exp.addEventListener('input', () => { if (state.last) writeLetter(); });
  ['fc-sched', 'fc-actual'].forEach((id) => { const el = $(id); if (el) el.addEventListener('input', syncClock); });

  // Запоминаем только безобидное: сценарий, причину и число пассажиров. Имя,
  // код бронирования и почта — персональные данные, их не сохраняем нигде.
  if (window.__toolSettings) {
    window.__toolSettings.bind(SLUG, $('fc-cause'), 'cause', 'unknown');
    window.__toolSettings.bind(SLUG, $('fc-pax'), 'pax', '1');
  }

  syncScenarioFields();
  recompute();
  stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
